import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { APP_DETAILS_TAB, AvailableLanguages, getAppDetailsTabNameByTabIndex } from '@constants';
import {
  AllDeviceInfo,
  ChangeRoutePayload,
  InAppNotification,
  InAppNotificationType,
  ROLES_KEYS,
  User,
} from '@interfaces';
import { TranslateService } from '@ngx-translate/core';
import { AnalyticsService } from '@core/services/analytics.service';
import { DeviceInfoService } from '@core/services/device-info.service';
import { CVMFacade } from '@state/cvm';
import { ManagerFacade } from '@state/manager';
import { ManagementFacade } from '@state/management';
import { InAppNotificationsFacade } from '@state/notifications';
import { RouterFacade } from '@state/router';
import { UserAuthFacade } from '@state/user-auth';
import { UserProcessFacade } from '@state/user-process';
import { MyWorkPermitsFacade } from '@state/my-work-permits';
import { debounce } from 'lodash-es';
import { Observable, ReplaySubject } from 'rxjs';
import { filter, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import { ChatService } from '../chat/chat.service';
import { getNotificationRoute, setNotificationViewOpts } from './notification.config';
import { StackedMessagesService } from '@shared/stacked-messages/stacked-messages.service';
import { UsersFacade } from '@state/users';
import { getNotificationDefinition } from './notifications.registry';
import { ConfirmationModalService } from '@shared/confirmation-modal/confirmation-modal.service';

@Component({
  selector: 'app-notifications',
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss'],
  standalone: false,
})
export class NotificationsComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly inAppNotifications = inject(InAppNotificationsFacade);
  private readonly routerFacade = inject(RouterFacade);
  private readonly userAuthFacade = inject(UserAuthFacade);
  private readonly userProcessFacade = inject(UserProcessFacade);
  private readonly myWorkPermitsFacade = inject(MyWorkPermitsFacade);
  private readonly cvmFacade = inject(CVMFacade);
  private readonly managerFacade = inject(ManagerFacade);
  private readonly router = inject(Router);
  private readonly managementFacade = inject(ManagementFacade);
  private readonly usersFacade = inject(UsersFacade);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly chatService = inject(ChatService);
  private readonly translateService = inject(TranslateService);
  private readonly deviceInfoService = inject(DeviceInfoService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly stackedMessagesService = inject(StackedMessagesService);
  private readonly confirmationModal = inject(ConfirmationModalService);

  private destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  @ViewChild('notificationsMainWindow') notificationsMainWindow: ElementRef<HTMLElement>;
  @ViewChild('notificationsList') notificationsList: ElementRef<HTMLElement>;

  @Output() closeNotifications: EventEmitter<void> = new EventEmitter<void>();

  public loading$: Observable<boolean> = this.inAppNotifications.loading$;
  public loadingMore$: Observable<boolean> = this.inAppNotifications.loadingMore$;
  public shortlistPagination$ = this.inAppNotifications.shortlistPagination$;
  public allNotificationsLoaded = false;

  public loadingChangesIn = '';
  public shortlist: InAppNotification[];
  public InAppNotificationType = InAppNotificationType;
  public showOnlyUnread = false;
  public currentLang: AvailableLanguages;
  public myself: Partial<User>;

  public deviceInfo: AllDeviceInfo;
  public ROLES_KEYS = ROLES_KEYS;

  private boundClickListener = this.clickListener.bind(this);
  private debouncedOnscroll = debounce(({ target, srcElement }) => {
    const { clientHeight } = srcElement as HTMLElement;
    const { scrollTop, scrollHeight } = target as HTMLElement;

    const shouldLoadMore = scrollHeight - scrollTop < clientHeight + 100;
    if (shouldLoadMore) {
      this.loadShortlistMore();
    }
  }, 150);

  @HostListener('document:keydown.escape', ['$event']) onKeydownHandler(event: Event): void {
    const { key, code } = event as KeyboardEvent;
    if (key.toLowerCase() === 'escape' || code.toLowerCase() === 'escape') {
      this.closeNotifications.emit();
    }
  }

  public ngOnInit(): void {
    this.deviceInfo = this.deviceInfoService.getInfo();

    this.deviceInfoService.infoEmitter.pipe(takeUntil(this.destroy$)).subscribe(info => {
      this.deviceInfo = info;
    });

    this.currentLang = this.translateService.currentLang as AvailableLanguages;
    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(({ lang }) => {
      this.currentLang = lang as AvailableLanguages;
    });

    this.loadingChangesIn = '';
    this.inAppNotifications.getShortlist(this.showOnlyUnread);
    this.inAppNotifications.shortlist$
      .pipe(
        filter(shortlist => !!shortlist?.length),
        withLatestFrom(this.userAuthFacade.myself$.pipe(filter(user => !!user?.id && !!user?.role?.key))),
        takeUntil(this.destroy$)
      )
      .subscribe(([rawShortlist, myself]) => {
        this.myself = myself;
        this.shortlist = this.stackRepeatedNotifications(rawShortlist).map(notif =>
          setNotificationViewOpts(notif, myself, this.translateService)
        );
      });

    this.inAppNotifications.markAllAsReadSuccess$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.stackedMessagesService.showInfo({ message: 'COMMON.NOTIFICATION.NOTIFICATIONS_UPDATED' });
    });
  }

  public ngAfterViewInit(): void {
    document.addEventListener('click', this.boundClickListener);
    this.notificationsList?.nativeElement.addEventListener('scroll', this.debouncedOnscroll);

    queueMicrotask(() => {
      this.notificationsMainWindow.nativeElement.focus();
    });
  }

  public ngOnDestroy(): void {
    document.removeEventListener('click', this.boundClickListener);
    this.notificationsList?.nativeElement.removeEventListener('scroll', this.debouncedOnscroll);
    this.debouncedOnscroll.cancel();
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public async notificationClick(notification: InAppNotification, $event: MouseEvent): Promise<any> {
    if (!notification.clickable) {
      return;
    }

    $event.preventDefault();
    $event.stopPropagation();
    this.analyticsService.trackEvent('user_event', 'user_clicked_notification', { type: notification.type });

    const { relatedCandidateVerificationId, relatedForeignerId, relatedUserDocumentId, relatedUserProcessId } =
      notification;

    if (notification.read === false) {
      await this.markAsRead(notification);
    }

    const notificationDefinition = getNotificationDefinition(notification.type);
    const notificationRoute = getNotificationRoute(notification, this.myself, notification.clickable);

    if (!notificationDefinition) {
      console.error('NO_CALLBACK_FOR_NOTIFICATION_TYPE');
      return;
    }

    const currPath = this.router.url.split('?')[0].slice(1);
    const queryParams = this.activatedRoute.snapshot.queryParams;
    let appDetailsTab = queryParams.appDetailsTab;
    const tabIndex = queryParams.tabIndex;
    if (tabIndex !== undefined) {
      appDetailsTab = getAppDetailsTabNameByTabIndex(Number(tabIndex));
    }
    if (!tabIndex && !appDetailsTab) {
      appDetailsTab = APP_DETAILS_TAB.GENERAL;
    }
    const isInGeneralView = appDetailsTab === APP_DETAILS_TAB.GENERAL;
    const isInDocsView = appDetailsTab === APP_DETAILS_TAB.DOCS;
    const isInSummaryView = appDetailsTab === APP_DETAILS_TAB.SUMMARY;

    switch (notificationDefinition.actionKey) {
      case 'management-application-summary-refresh': {
        if (notificationRoute) {
          const shouldRefresh = currPath === this.getRoutePath(notificationRoute) && isInSummaryView;
          if (shouldRefresh) {
            this.managementFacade.getUserProcess({ userId: relatedForeignerId, userProcessId: relatedUserProcessId });
          }
          this.changeNotificationRoute(notificationRoute);
        }
        return;
      }
      case 'management-application-docs-refresh': {
        if (notificationRoute) {
          const shouldRefresh = currPath === this.getRoutePath(notificationRoute) && isInDocsView;
          if (shouldRefresh) {
            this.managementFacade.getUserProcessDocuments({
              userId: relatedForeignerId,
              userProcessId: relatedUserProcessId,
            });
          }
          this.changeNotificationRoute(notificationRoute);
        }
        return;
      }
      case 'management-application-general-refresh': {
        if (notificationRoute) {
          const shouldRefresh = currPath === this.getRoutePath(notificationRoute) && isInGeneralView;
          if (shouldRefresh) {
            this.usersFacade.getForeignerBasicData(relatedForeignerId);
            this.managementFacade.getUserProcess({ userId: relatedForeignerId, userProcessId: relatedUserProcessId });
          }
          this.changeNotificationRoute(notificationRoute);
        }
        return;
      }
      case 'management-application-general-path-refresh': {
        if (notificationRoute) {
          const shouldRefresh = currPath === this.getRoutePath(notificationRoute);
          if (shouldRefresh) {
            this.usersFacade.getForeignerBasicData(relatedForeignerId);
            this.managementFacade.getUserProcess({ userId: relatedForeignerId, userProcessId: relatedUserProcessId });
          }
          this.changeNotificationRoute(notificationRoute);
        }
        return;
      }
      case 'management-application-pre-survey-refresh': {
        if (notificationRoute) {
          const shouldRefresh = currPath === this.getRoutePath(notificationRoute);
          if (shouldRefresh) {
            this.usersFacade.getForeignerBasicData(relatedForeignerId);
          }
          this.changeNotificationRoute(notificationRoute);
        }
        return;
      }
      case 'management-application-summary-review-items-refresh': {
        if (notificationRoute) {
          const shouldRefresh = currPath === this.getRoutePath(notificationRoute) && isInSummaryView;
          if (shouldRefresh) {
            this.managementFacade.getUserProcess({ userId: relatedForeignerId, userProcessId: relatedUserProcessId });
          }
          this.changeNotificationRoute(notificationRoute);
        }
        return;
      }
      case 'user-process-route-or-reload': {
        if (notificationRoute) {
          this.closeNotifications.emit();
          const shouldRefresh = this.router.url.includes(this.getRoutePath(notificationRoute));
          if (shouldRefresh) {
            const urlParts = window.location.href.split('/');
            urlParts.pop();
            window.location.href = urlParts.join('/');
          } else {
            this.routerFacade.changeRoute(notificationRoute);
          }
        }
        return;
      }
      case 'user-process-summary-comments-route-or-reload': {
        if (notificationRoute) {
          this.closeNotifications.emit();
          const shouldRefresh = this.router.url.includes(this.getRoutePath(notificationRoute));
          if (shouldRefresh) {
            const urlParts = window.location.href.split('/');
            urlParts.pop();
            window.location.href = `${urlParts.join('/')}/summary?showDocumentsVerificationComment=true`;
          } else {
            this.routerFacade.changeRoute(notificationRoute);
          }
        }
        return;
      }
      case 'user-process-documents-refresh': {
        if (notificationRoute) {
          this.closeNotifications.emit();
          const shouldRefresh = this.router.url.includes(this.getRoutePath(notificationRoute));
          if (shouldRefresh) {
            this.userProcessFacade.getMyProcessDetails(relatedUserProcessId);
            this.userProcessFacade.getListOfDocuments(relatedUserProcessId);
          } else {
            this.routerFacade.changeRoute(notificationRoute);
          }
        }
        return;
      }
      case 'user-chat': {
        this.closeNotifications.emit();
        if (this.deviceInfo.deviceTypeDetected === 'DESKTOP') {
          this.chatService.showChat$.next({ show: true, relatedForeignerId });
        } else {
          await this.router.navigate(['/chat']);
        }
        return;
      }
      case 'hard-user-process': {
        this.closeNotifications.emit();
        window.location.href = '/user-process/' + relatedUserProcessId;
        return;
      }
      case 'partner-payouts': {
        if (notificationRoute) {
          this.closeNotifications.emit();
          const shouldRefresh = currPath === this.getRoutePath(notificationRoute);
          if (shouldRefresh) {
            window.location.href = '/partners/payouts';
          } else {
            this.routerFacade.changeRoute(notificationRoute);
          }
        }
        return;
      }
      case 'candidate-verification': {
        if (
          this.handleCandidateVerificationNotificationRoute(notificationRoute, currPath, relatedCandidateVerificationId)
        ) {
          return;
        }
        if (notificationRoute) {
          this.changeNotificationRoute(notificationRoute);
          return;
        }
        console.error('NO_CALLBACK_FOR_NOTIFICATION_TYPE');
        return;
      }
      case 'my-work-permit-refresh': {
        if (notificationRoute) {
          this.closeNotifications.emit();
          // The list feeds the application selector (e.g. a newly linked work-permit).
          this.myWorkPermitsFacade.getMyWorkPermits();
          const shouldRefresh = currPath === this.getRoutePath(notificationRoute);
          if (shouldRefresh) {
            this.myWorkPermitsFacade.getMyWorkPermit(notification.relatedWorkPermitId);
          } else {
            this.routerFacade.changeRoute(notificationRoute);
          }
        }
        return;
      }
      case 'noop': {
        this.closeNotifications.emit();
        return;
      }
      case 'route-only': {
        if (notificationRoute) {
          this.changeNotificationRoute(notificationRoute);
          return;
        }
        console.error('NO_CALLBACK_FOR_NOTIFICATION_TYPE');
        return;
      }
      default:
        console.error('NO_CALLBACK_FOR_NOTIFICATION_TYPE');
        return;
    }
  }

  public setShowOnlyUnread(value: boolean): void {
    this.showOnlyUnread = value;
    this.shortlist = [];
    this.inAppNotifications.getShortlist(value);
  }

  public markAsRead(notification: InAppNotification): Promise<any> {
    this.loadingChangesIn = notification.id;
    const promiseToResolve = new Promise(resolve => {
      this.inAppNotifications.markAsReadSuccess$.pipe(take(1), takeUntil(this.destroy$)).subscribe(() => {
        setTimeout(() => {
          this.loadingChangesIn = '';
          return resolve(true);
        });
      });
    });

    const ids = notification.stackedIds?.length ? [...notification.stackedIds] : [notification.id];
    this.inAppNotifications.markAsRead(ids);
    return promiseToResolve;
  }

  public markAsUnread(notification: InAppNotification): void {
    this.loadingChangesIn = notification.id;

    this.inAppNotifications.markAsUnreadSuccess$.pipe(take(1), takeUntil(this.destroy$)).subscribe(() => {
      this.loadingChangesIn = '';
    });

    const ids = notification.stackedIds?.length ? [...notification.stackedIds] : [notification.id];
    this.inAppNotifications.markAsUnRead(ids);
  }

  public markAllAsRead(): void {
    this.confirmationModal
      .open({
        confirmationToTranslate: 'IN_APP_NOTIFICATIONS.CONFIRM_MARK_ALL_AS_READ',
      })
      .afterClosed()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(confirmed => {
        if (!confirmed) {
          return;
        }

        this.inAppNotifications.markAllAsRead();
      });
  }

  public loadShortlistMore(): void {
    this.shortlistPagination$.pipe(take(1), takeUntil(this.destroy$)).subscribe(pagination => {
      // we've loaded all of the notifications
      if (pagination.count >= pagination.all) {
        this.allNotificationsLoaded = true;
        return;
      }

      this.allNotificationsLoaded = false;
      this.inAppNotifications.getShortlistMore({
        ...pagination,
        offset: pagination.offset + pagination.limit,
      });
    });
  }

  public trackByFn(index: number, notification: InAppNotification): string {
    return notification.id;
  }

  private clickListener($event: MouseEvent): void {
    const htmlTarget = $event.target instanceof Element ? $event.target : null;

    if (!this.notificationsMainWindow?.nativeElement || !htmlTarget) {
      return;
    }

    // if click has been inside window - do nothing
    if (this.notificationsMainWindow.nativeElement.contains(htmlTarget)) {
      return;
    }

    if (htmlTarget.closest('.confirmation-modal')) {
      return;
    }

    if (htmlTarget.closest('.open-notifications-button')) {
      return;
    }

    this.closeNotifications.emit();
  }

  private stackRepeatedNotifications(notifications: InAppNotification[]): InAppNotification[] {
    const stacked: InAppNotification[] = [];

    const NO_STACK_TYPES = [
      InAppNotificationType.FOREIGNER_CHANGED_PLANNED_DOCUMENTS_SIGNING_MODE,
      InAppNotificationType.FOREIGNER_CHANGED_PLANNED_DOCUMENTS_SIGNING_DATE,
      InAppNotificationType.FOREIGNER_CANCELLED_VISIT_IN_COMPANY_OFFICE,
      InAppNotificationType.FOREIGNER_MADE_PURCHASE,
      InAppNotificationType.REASSIGNED_FOREIGNERS_TO_YOU,
      InAppNotificationType.NEW_APPLICATION_HAS_BEEN_ADDED,
      InAppNotificationType.NEW_APPLICATION_HAS_BEEN_ADDED_WITH_TYPE,
      InAppNotificationType.APPLICATION_HAS_BEEN_DELETED,
      InAppNotificationType.FOREIGNER_SET_IMMIGRATION_OFFICE_VISIT_DATE,
      InAppNotificationType.FOREIGNER_CHANGED_IMMIGRATION_OFFICE_VISIT_DATE,
      InAppNotificationType.PARTNER_REQUESTED_PAYOUT,
      InAppNotificationType.EMPLOYEE_CANCELLED_VOIVODESHIP_OFFICE_APPOINTMENT,
      InAppNotificationType.CANDIDATE_REQUESTED_EXTENDING_TIME_FOR_FILLING_FORM,
      InAppNotificationType.CANDIDATE_REQUESTED_STOPPING_VERIFICATION,
      InAppNotificationType.CANDIDATE_VERIFICATION_DELETED,
      InAppNotificationType.CONSULTANT_CREATED_APPOINTMENT,
      InAppNotificationType.CONSULTANT_ACCEPTED_APPOINTMENT_DATE,
      InAppNotificationType.CONSULTANT_REJECTED_APPOINTMENT_DATE,
      InAppNotificationType.CONSULTANT_CANCELLED_APPOINTMENT,

      InAppNotificationType.FOREIGNER_REQUESTED_APPOINTMENT_DATE,
      InAppNotificationType.FOREIGNER_CHANGED_REQUESTED_APPOINTMENT,
      InAppNotificationType.FOREIGNER_CANCELLED_APPOINTMENT,

      InAppNotificationType.WORK_PERMIT_DOCUMENT_APPROVED,
      InAppNotificationType.WORK_PERMIT_DOCUMENT_REJECTED,
    ];

    notifications.forEach((notification, currIndex) => {
      if (currIndex === 0) {
        stacked.push(notification);
        return;
      }

      const prevNotif = stacked[stacked.length - 1];

      if (
        NO_STACK_TYPES.includes(notification.type) === false &&
        prevNotif.type === notification.type &&
        prevNotif.relatedForeignerId === notification.relatedForeignerId &&
        prevNotif.relatedUserProcessId === notification.relatedUserProcessId &&
        prevNotif.relatedUserDocumentId === notification.relatedUserDocumentId &&
        prevNotif.relatedCandidateVerificationId === notification.relatedCandidateVerificationId &&
        prevNotif.relatedWorkPermitId === notification.relatedWorkPermitId &&
        prevNotif.read === notification.read
      ) {
        const stackedNotif: InAppNotification = {
          ...notification,
          stacked: true,
          stackSize: prevNotif.stackSize ? prevNotif.stackSize + 1 : 2,
          stackedIds: prevNotif.stackedIds
            ? [notification.id, ...prevNotif.stackedIds]
            : [notification.id, prevNotif.id],
        };
        stacked[stacked.length - 1] = stackedNotif;
        return;
      }

      stacked.push(notification);
    });

    return stacked;
  }

  private getRoutePath(changeRoutePayload: ChangeRoutePayload): string {
    return changeRoutePayload.linkParams.join('/');
  }

  private changeNotificationRoute(changeRoutePayload: ChangeRoutePayload): void {
    this.closeNotifications.emit();
    this.routerFacade.changeRoute(changeRoutePayload);
  }

  private isCandidateVerificationRoute(changeRoutePayload: ChangeRoutePayload): boolean {
    const [section] = changeRoutePayload.linkParams;
    return section === 'manager' || section === 'cvm';
  }

  private handleCandidateVerificationNotificationRoute(
    changeRoutePayload: ChangeRoutePayload | null,
    currPath: string,
    candidateVerificationId: string | null
  ): boolean {
    if (!changeRoutePayload || !this.isCandidateVerificationRoute(changeRoutePayload)) {
      return false;
    }

    const pathForNotification = this.getRoutePath(changeRoutePayload);
    const shouldRefresh =
      currPath === pathForNotification && this.matchesCandidateVerificationQuery(changeRoutePayload);

    if (shouldRefresh && candidateVerificationId) {
      this.refreshCandidateVerificationDetails(changeRoutePayload, candidateVerificationId);
    }

    this.changeNotificationRoute(changeRoutePayload);
    return true;
  }

  private matchesCandidateVerificationQuery(changeRoutePayload: ChangeRoutePayload): boolean {
    const desiredTab = changeRoutePayload.extras?.queryParams?.tab;

    if (!desiredTab) {
      return true;
    }

    return this.activatedRoute.snapshot.queryParams.tab === desiredTab;
  }

  private refreshCandidateVerificationDetails(
    changeRoutePayload: ChangeRoutePayload,
    candidateVerificationId: string
  ): void {
    const section = changeRoutePayload.linkParams[0];

    if (section === 'manager') {
      this.managerFacade.getCandidateVerificationDetails(candidateVerificationId);
      return;
    }

    if (section === 'cvm') {
      this.cvmFacade.getCandidateVerificationDetails(candidateVerificationId);
    }
  }
}
